import Button from "@/common/components/Button";
import Modal from "@/common/components/Modal";
import ResumeItem from "@/common/components/ResumeItem";
import SelectController from "@/common/components/SelectController";
import Spinner from "@/common/components/Spinner";
import TextField from "@/common/components/TextField";
import useDebounce from "@/common/hooks/useDebounce";
import useModalMessagesSimple from "@/common/hooks/useModalMessagesSimple";
import { cn } from "@/common/utils/cn";
import { FormTransformers } from "@/common/utils/formTransformers";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { PlusIcon, SearchIcon } from "lucide-react";
import { createElement, useEffect, useMemo, useState } from "react";
import { DateRange } from "react-day-picker";
import { SubmitHandler, useForm, useWatch } from "react-hook-form";
import { dashboardFilters } from "../../constants/dashboards/dashboardFilters.constant";
import { dashboardChartComponent, dashboardKind } from "../../constants/dashboards/dashboardKind.constant";
import { useCreateDashboardComponent } from "../../hooks/dashboards/useCreateDashboardComponent";
import useDashboardComponentParsedFilters from "../../hooks/dashboards/useDashboardComponentParsedFilters";
import { useDashboardComponentsGenerators } from "../../hooks/dashboards/useDashboardComponentsGenerators";
import { useDashboardComponentsPreview } from "../../hooks/dashboards/useDashboardComponentsPreview";
import { createDashboardComponentSchema, CreateDashboardComponentValidationSchema } from "../../schemas/dashboards/dashboardComponent";
import { DashboardFilters } from "../../types/dashboards/dashboard.type";
import { DashboardComponentDateFilters, DashboardComponentFiltersParams, DashboardComponentGeneratorData } from "../../types/dashboards/dashboardComponent.type";

interface CreateDashboardComponentProps {
  dashboardId: string;
  initialPosition: {
    x: number;
    y: number;
  };
  onCreateComponent?: () => void;
  width?: number;
  filters?: DashboardComponentFiltersParams & {
    date: DateRange | undefined;
  };
  dashboardDesiredFilters?: DashboardFilters[];
}

const CreateDashboardComponent = ({
  dashboardId,
  initialPosition,
  onCreateComponent,
  width,
  filters,
  dashboardDesiredFilters,
}: CreateDashboardComponentProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const { setMessages } = useModalMessagesSimple({
    isError: true,
  });

  const [search, setSearch] = useDebounce('');

  const [selectedGenerator, setSelectedGenerator] = useState<DashboardComponentGeneratorData>();

  const queryClient = useQueryClient();

  const {
    control,
    setValue,
    setError,
    handleSubmit,
    reset,
  } = useForm<CreateDashboardComponentValidationSchema>({
    resolver: zodResolver(createDashboardComponentSchema),
  });

  const watchGeneratorKlass = useWatch({
    control,
    name: "generator_klass",
  });

  const watchKind = useWatch({
    control,
    name: 'kind',
  });

  const definitionFilters = selectedGenerator?.filters ?? {};

  const transformedFilters = useDashboardComponentParsedFilters({
    filters: filters ?? { date: undefined },
    dashboardDesiredFilters: dashboardDesiredFilters || [],
    definitionFilters,
  });

  const {
    data: generators,
    dataUpdatedAt: generatorsUpdatedAt,
  } = useDashboardComponentsGenerators<DashboardComponentGeneratorData[]>({
    filters: {
      search,
    },
  });

  const {
    data: dashboardComponentPreview,
    isPending: dashboardComponentPreviewIsPending,
  } = useDashboardComponentsPreview({
    filters: {
      generatorKlass: selectedGenerator?.klass,
      kind: watchKind?.value,
      filters: transformedFilters,
    }
  });

  const kindOptions = useMemo(() => {
    if (!watchGeneratorKlass || !selectedGenerator) {
      return [];
    }

    return selectedGenerator.available_kinds?.map((kind) => ({
      value: kind,
      label: dashboardKind[kind] || kind,
    }));
  }, [watchGeneratorKlass]);

  const {
    mutate: createComponent,
    isPending: createComponentIsPending,
  } = useCreateDashboardComponent({
    dashboardId: dashboardId,
    initialPosition,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["dashboard", dashboardId],
        });

        setIsOpen(false);

        onCreateComponent?.();
      },
      onError: FormTransformers.errorsTransformer({
        setError,
        setMessages,
      }),
    },
  });

  const onSubmit: SubmitHandler<CreateDashboardComponentValidationSchema> = (
    data,
  ) => createComponent(data);

  const filteredGenerators = useMemo(() => {
    if (!search) {
      return generators;
    }

    const filteredDashboards = generators.filter((generator) => {
      return generator.title.toUpperCase().includes(search.toUpperCase());
    });

    return filteredDashboards;
  }, [generatorsUpdatedAt, search]);

  useEffect(() => {
    reset();
    setSelectedGenerator(undefined);
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      Trigger={
        <Button className="text-nowrap h-10">
          {width && width < 700 ? (
            <PlusIcon size={16} />
          ) : 'Adicionar gráfico'}
        </Button>
      }
      title="Novo gráfico"
      className="rounded-lg overflow-hidden w-[90vw] max-w-[920px] h-[90vh] max-h-[760px] bg-black-900 flex flex-col"
      bodyClassName="text-white-800 flex-1 flex flex-col overflow-hidden"
      showClose={false}
      useCancellableFooter
      cancelButtonClassName="bg-white-100"
      confirmButtonClassName="bg-primary-blue"
      onConfirm={handleSubmit(onSubmit)}
      confirmButtonIsLoading={createComponentIsPending}
      body={
        <form className="h-full flex-1 overflow-hidden flex gap-3">
          <div className="flex-1 flex flex-col overflow-hidden gap-3">
            <TextField
              LeftIcon={<SearchIcon />}
              placeholder="Pesquisar"
              className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
              onChange={(e: any) => setSearch(e.target.value)}
            />

            <div className="flex flex-col gap-2 overflow-auto thin-scroll pr-1">
              {filteredGenerators.map((generator) => (
                <div
                  className={cn(
                    'bg-white-100 hover:bg-white-200 transition-colors cursor-pointer rounded-md p-3 flex flex-col gap-1',
                    selectedGenerator?.klass === generator.klass && 'bg-white-200 border border-white-300'
                  )}
                  onClick={() => {
                    setSelectedGenerator(generator);

                    setValue('generator_klass', {
                      value: generator.klass,
                      label: generator.title,
                    });

                    setValue('kind', {
                      value: generator.available_kinds[0],
                      label: dashboardKind[generator.available_kinds[0]],
                    });
                  }}
                >
                  <p className="text-sm">
                    {generator.title}
                  </p>

                  {generator.subtitle && (
                    <p className="text-xs text-white-300">
                      {generator.subtitle}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="h-full w-px bg-neutral-op-8" />

          <div className="flex-1 flex flex-col gap-3 overflow-hidden">
            {selectedGenerator && (
              <>
                <div className="flex flex-col gap-2">
                  <p className="font-bold">
                    Pré-visualização
                  </p>

                  <div className="bg-white-200 overflow-hidden rounded-lg h-64 flex flex-col">
                    <div className="p-4 flex flex-col gap-1">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex flex-col">
                          <p className="text-white-400 text-sm font-semibold select-none">
                            {selectedGenerator.title}
                          </p>

                          {selectedGenerator.subtitle && (
                            <p className="text-white-300 text-xs font-semibold select-none">
                              {selectedGenerator.subtitle}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div
                      key={`preview-${selectedGenerator.klass}`}
                      className={cn(
                        'flex-1 overflow-auto',
                        (dashboardComponentPreviewIsPending || dashboardComponentPreview?.data?.data?.length === 0) && 'flex items-center justify-center px-4',
                      )}
                    >
                      {dashboardComponentPreviewIsPending ? (
                        <Spinner className="fill-white-200 text-white-100" />
                      ) : dashboardComponentPreview?.data?.data?.length === 0 ? (
                        <p className="text-white-300 italic text-sm text-center">
                          Não há dados para serem exibidos
                        </p>
                      ) : (dashboardComponentPreview?.data?.kind && createElement(
                        dashboardChartComponent[dashboardComponentPreview?.data?.kind],
                        {
                          chartData: dashboardComponentPreview?.data,
                          dashboardComponentUpdatedAt: 0,
                        },
                      ))}
                    </div>
                  </div>
                </div>

                <SelectController
                  control={control}
                  name="kind"
                  label="Tipo do gráfico"
                  markAsRequired
                  options={kindOptions}
                />

                <div className="flex flex-col gap-3 overflow-auto thin-scroll">
                  <ResumeItem
                    className="text-sm"
                    label="Título"
                    value={selectedGenerator.title}
                  />

                  {selectedGenerator.subtitle && (
                    <ResumeItem
                      className="text-sm"
                      label="Descrição"
                      value={selectedGenerator.subtitle}
                    />
                  )}

                  {Object.keys(selectedGenerator.filters).length > 0 && (
                    <ResumeItem
                      className="text-sm"
                      label="Filtros disponíveis"
                      value={Object.keys(selectedGenerator.filters).map((value) => {
                        if (value === DashboardComponentDateFilters.StartDate) {
                          return `• ${dashboardFilters[DashboardFilters.DateRange]}`;
                        }

                        if (value === DashboardComponentDateFilters.EndDate) {
                          return null;
                        }

                        const filterLabel = dashboardFilters[value as DashboardFilters];

                        if (!filterLabel) {
                          return null;
                        }

                        return `• ${filterLabel}`;
                      }).filter(Boolean).join('\n')}
                      valueClassName="whitespace-pre-wrap"
                    />
                  )}
                </div>
              </>
            )}

            {!selectedGenerator && (
              <div className="flex-1 flex items-center justify-center">
                <p className="text-white-400 italic">
                  Selecione o gráfico na lista à esquerda
                </p>
              </div>
            )}
          </div>
        </form>
      }
    />
  );
};

export default CreateDashboardComponent;
